<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div class="steps-container">
          <div id="image-holder" class="image-holder" ref="imageHolder"></div>
          <div class="step" v-if="!!currentStep">
            <div class="td-holder" v-if="isCompleteWordStep" transition="fade">
              <div class="big-wrap bigText" :id="`section-${currentStep.id}`">
                <color-block-text
                  :is-simple="true"
                  :text="currentRound.word"
                  :answer="currentRound.letterCombo"
                  :text-step="currentRound?.repetitions - 1 ?? 5"
                >
                </color-block-text>
              </div>
            </div>
            <div class="td-holder" transition="fade">
              <div
                class="normal-wrap normalText"
                v-for="(step, index) of currentRound?.steps"
                :id="`section-${step.id}`"
                :key="step.id"
              >
                <color-block-text :is-simple="true" :text="step.answer" :text-step="index">
                </color-block-text>
              </div>
            </div>
          </div>
          <article :class="{ 'curtain-container': true, isMobile }">
            <div class="curtain"></div>
          </article>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import AccessibilityHelper from 'mixins/AccessibilityHelper';

import ActivityStage from 'components/activity/ActivityStage.vue';
import ColorBlockText from 'components/text-displays/ColorBlockText.vue';

export default {
  components: { ActivityStage, ColorBlockText },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger, AccessibilityHelper],

  data() {
    return {
      imageHolder: null,
      activityName: 'CurtainCall',
      isCompleteWordStep: false
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      this.createRounds();
      await this.loadImages();
      await this.loadSounds();
    },

    async loadImages() {
      this.rounds.forEach(({ monochromeImage, colorImage }) => {
        this.queueImage(monochromeImage.image.src);
        this.queueImage(colorImage.image.src);
      });
      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(({ wordAudio }) => {
        this.queueAudio(wordAudio.audio.src);
      });
      this.queueAudio(
        this.rounds.map(({ wordAudio }) => {
          return wordAudio.audio.src;
        })
      );

      await this.downloadAudio();
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(() => {
        this.setActivityContainerHeight();
        this.setCurtainHeight();
      });

      this.setImageBehindCurtain(this.currentRound.monochromeImage.image.src);
      this.rerenderScreenReaderAlert(
        'Type each letter combo to pull back the curtain and reveal the hidden word.'
      );
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.finishStep();
      }
    },

    finishStep() {
      if (this.isCompleteWordStep) {
        const { src } = this.currentRound.wordAudio.audio;
        this.sndBellAndAfterPlayOtherHowl(this.cache.get(src));
      } else {
        this.sndBell();
      }

      this.completeStep();

      const element = document.getElementById(`section-${this.currentStep.id}`);

      if (this.isLastStepInRound) {
        TweenMax.set(element, { css: { visibility: 'hidden' } });
        TweenMax.to('.curtain', 1, {
          css: { left: '100%' },
          onComplete: () => {
            TweenMax.to('.curtain', 1, {
              css: { left: '0px' },
              delay: 1,
              onComplete: () => {
                const imageSrc = this.isLastStepInRound
                  ? this.currentRound.colorImage.image.src
                  : this.currentRound.monochromeImage.image.src;
                this.setImageBehindCurtain(imageSrc);
                if (this.isCompleteWordStep) {
                  this.rerenderScreenReaderAlert(
                    `The word behind the curtain - ${this.currentRound.word}.`
                  );
                  this.handleRoundEnd();
                  this.isCompleteWordStep = false;
                  return;
                }
                this.overwriteProgressText({
                  currentText: this.currentRound.letterCombo
                });
                this.rerenderScreenReaderAlert(
                  this.getTouchHoldBasedInstructions(
                    `Type the letter combo - ${this.currentRound.letterCombo} - one more time to reveal the word. What will it be?`
                  )
                );

                this.isCompleteWordStep = true;
              }
            });
          }
        });
      } else {
        const xPosition = element.offsetLeft + element.offsetWidth;
        TweenMax.to(element, 0.5, { autoAlpha: 0 });
        TweenMax.to('.curtain', 1, { css: { left: xPosition } });
        this.setCurrentStep();
      }
    },

    handleRoundEnd() {
      if (this.isEndOfActivity) TweenMax.delayedCall(2.5, this.completeActivity);
      else
        TweenMax.delayedCall(1.5, () => {
          this.setupNewRound();
          this.setImageBehindCurtain(this.currentRound.monochromeImage.image.src);
        });
    },

    setCurtainHeight() {
      const activityStageEl = document.querySelector('.activity-stage');
      const curtainContainerEl = document.querySelector('.curtain-container');

      if (activityStageEl && curtainContainerEl) {
        curtainContainerEl.style.height = `${activityStageEl.clientHeight - 45}px`;
        curtainContainerEl.children.item(0).style.height = `${activityStageEl.clientHeight - 45}px`;
      }
    },

    setImageBehindCurtain(imageSrc) {
      this.$refs.imageHolder.style.backgroundImage = `url(${imageSrc || ''})`;
      this.$refs.imageHolder.style.backgroundRepeat = 'no-repeat';
      this.$refs.imageHolder.style.backgroundPosition = 'top center';
    }
  },

  watch: {
    expectedKey() {
      const { stepIndex } = this.progress;
      const { currentIndex } = this.typingProgress;

      if (currentIndex) return;

      const letterCombo = this.currentRound?.steps?.[stepIndex]?.answer;
      const timeOrTimes = `time${this.currentRound?.steps.length - stepIndex > 1 ? 's' : ''}`;

      if (letterCombo) {
        this.setTotalKeyboardingSegment(letterCombo);
        this.rerenderScreenReaderAlert(
          this.getTouchHoldBasedInstructions(
            `Type the letter combo - ${letterCombo} - ${this.currentRound?.steps.length -
              stepIndex} ${timeOrTimes}.`
          )
        );
      }
    }
  }
};
</script>
